// i18n configuration with react-i18next
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { enGrmsTranslations, enHousekeepingTranslations, hrGrmsTranslations, hrHousekeepingTranslations, } from './grmsTranslations.js';
// =============================================================================
// English translations (default)
// =============================================================================
const enTranslations = {
    // Default labels of the shared console kit (@bms/common/ui). Every
    // primitive that ships a word of its own reads it from here, so the kit
    // renders in Croatian without each screen re-translating "Cancel".
    kit: {
        cancel: 'Cancel',
        confirm: 'Confirm',
        close: 'Close',
        save: 'Save',
        undo: 'Undo',
        edit: 'Edit',
        delete: 'Delete',
        remove: 'Remove',
        copy: 'Copy',
        copied: 'Copied',
        retry: 'Retry',
        discard: 'Discard',
        keepEditing: 'Keep editing',
        discardCount_one: 'Discard {{count}} change?',
        discardCount_other: 'Discard {{count}} changes?',
        changeCount_one: '{{count}} change',
        changeCount_other: '{{count}} changes',
        search: 'Search…',
        selectPlaceholder: 'Select…',
        clearSelection: 'Clear selection',
        clearFilters: 'Clear filters',
        date: 'Date',
        time: 'Time (24-hour)',
        noMatches: 'No matches',
        noMatchesFor: 'Nothing matches “{{query}}”',
        noResultsTitle: 'Nothing matches these filters',
        emptyTitle: 'Nothing here yet',
        rowsPerPage: 'Rows per page',
        pageRange: '{{from}}–{{to}} of {{total}}',
        previousPage: 'Previous page',
        nextPage: 'Next page',
        selectAll: 'Select all',
        selectRow: 'Select row',
        expandRow: 'Expand row',
        collapseRow: 'Collapse row',
        selectedCount_one: '{{count}} selected',
        selectedCount_other: '{{count}} selected',
        moreActions: 'More actions',
        increment: 'Increase',
        decrement: 'Decrease',
        dropzoneTitle: 'Drop a file here or click to choose',
        dropzoneMultiple: 'Drop files here or click to choose',
        fileTooLarge: '{{name}} is larger than {{max}}',
        typeToConfirm: 'Type {{value}} to confirm',
        characters: '{{count}} / {{max}}',
        loading: 'Loading…',
        breadcrumb: 'breadcrumb',
        more: 'More',
        enterKey: 'Enter',
        escKey: 'Esc',
    },
    common: {
        loading: 'Loading...',
        error: 'Error',
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        create: 'Create',
        confirm: 'Confirm',
        search: 'Search',
        actions: 'Actions',
        status: 'Status',
        name: 'Name',
        type: 'Type',
        value: 'Value',
        enabled: 'Enabled',
        disabled: 'Disabled',
        yes: 'Yes',
        no: 'No',
        unknown: 'Unknown',
        noData: 'No data available',
        refresh: 'Refresh',
        back: 'Back',
        next: 'Next',
        previous: 'Previous',
        finish: 'Finish',
        close: 'Close',
    },
    auth: {
        login: 'Login',
        logout: 'Logout',
        username: 'Username',
        password: 'Password',
        loginTitle: 'Sign in',
        loginSuccess: 'Login successful!',
        usernameRequired: 'Please enter your username',
        passwordRequired: 'Please enter your password',
        loginError: 'Invalid username or password',
        sessionExpired: 'Your session has expired. Please login again.',
        siteSelection: 'Site selection',
        selectSite: 'Select a site…',
        selectSiteAction: 'Select site',
        loadingSites: 'Loading sites…',
        siteRequired: 'Please select a site',
        createNewSite: 'Create new site…',
        newSiteName: 'Site name',
        newSiteNamePlaceholder: 'e.g. Main Building',
        newSiteNameRequired: 'Please enter a site name',
        newSiteLocked: 'Profile and site category are locked after creation.',
        siteCreated: 'Site "{{name}}" created',
        noSiteAccessTitle: 'No site access',
        noSiteAccessBody: 'Your account is not assigned to any site. Ask an administrator to grant you access, then sign in again.',
        backToSignIn: 'Back to sign in',
        siteCategory: {
            hospitality: 'Hospitality',
            residential: 'Residential',
            commercial: 'Commercial',
        },
    },
    // "Activate a site" on an installation with no site yet. Refusal sentences are
    // BMS-spec docs/access/ui-display-rule.md §Site-service refusals.
    activation: {
        title: 'Activate a site',
        intro: 'This Edge has no site yet. Activate it with the codes Corteh gave you; Edge takes the site name, type and time zone from Corteh.',
        method: 'How to activate',
        setup: 'Setup code',
        setupHint: 'A new site: the Claim Code and the four-digit setup code from Corteh.',
        restore: 'Restore from backup',
        restoreHint: 'Rebuild a site on this fresh installation from its newest configuration backup at Corteh.',
        file: 'Site file',
        fileHint: 'The site file Corteh sent, when codes cannot be used.',
        claimCode: 'Claim Code',
        claimCodeHint: 'Eight letters, written XXXX-XXXX.',
        setupCode: 'Setup code',
        restoreCode: 'Restore code',
        codeHint: 'Four digits, used once.',
        activateAction: 'Activate site',
        restoreAction: 'Restore site',
        chooseFile: 'Choose site file…',
        copyDetails: 'Copy details',
        activated: 'Site {{site}} is activated',
        restored: 'Site {{site}} is restored from its backup',
        reissue: 'Corteh must finish the site key rotation, then all cards must be reissued.',
        signInAgain: 'The site is ready; sign in again to open it.',
        continue: 'Continue',
        noSiteTitle: 'This Edge has no site yet',
        noSiteBody: 'Activate its site first; everything else needs it.',
        refusal: {
            site_code_invalid: 'This code is incorrect or no longer usable; ask Corteh for a new code.',
            site_mode_mismatch: 'This site is registered for the other product; contact Corteh.',
            site_not_found: 'This site could not be found; check the Claim Code.',
            site_file_unavailable: 'Linda has no usable file for this site; contact Corteh.',
            backup_unavailable: 'No usable cloud backup is available; choose a local backup or contact Corteh.',
            backup_member_unknown: 'The backup contains unsupported information; update the application and try again.',
            backup_invalid: 'This configuration backup is invalid; keep the local copy and contact Corteh.',
            site_restore_not_empty: 'Activate or restore on a fresh installation; this one already contains configuration.',
            site_package_invalid: 'This site file is invalid; ask Corteh for a replacement.',
            site_package_claim_mismatch: 'This site file belongs to a different site.',
            site_certificate_invalid: "This site's certificate could not be verified; contact Corteh.",
            site_certificate_claim_mismatch: 'This certificate belongs to a different site.',
            site_certificate_signer_mismatch: 'This site file names another signing key; ask Corteh for a replacement.',
            signing_key_import_failed: 'This installation could not store the site key; contact Corteh.',
            rate_limited: 'Too many attempts were made; wait before trying again.',
            service_unavailable: 'Linda is unavailable; keep working locally and try this action later.',
            pending_token_not_permitted: 'Your sign-in expired; sign in again and repeat the activation.',
            not_authenticated: 'Your sign-in expired; sign in again and repeat the activation.',
            permission_denied: 'This account may not activate a site.',
            unknown: 'The site could not be activated; contact Corteh.',
        },
    },
    nav: {
        dashboard: 'Dashboard',
        desk: 'Desk',
        activity: 'Activity',
        rooms: 'Rooms',
        gateways: 'Gateways',
        provisioning: 'Provisioning',
        settings: 'Settings',
        siteConfig: 'Site Configuration',
        users: 'Users',
        home: 'Home',
        scenes: 'Scenes',
        housekeeping: 'Housekeeping',
        accessControl: 'Access Control',
        frontOffice: 'Front office',
        visitors: 'Visitors',
        cards: 'Cards',
        reports: 'Reports',
        'hotel-settings': 'Hotel settings',
        cardTapOpenSearch: 'Open search on card tap',
        switchToAdmin: 'Switch to Admin UI',
        logout: 'Log out',
    },
    status: {
        online: 'Online',
        offline: 'Offline',
        error: 'Error',
        updating: 'Updating',
        active: 'Active',
        inactive: 'Inactive',
        commissioning: 'Commissioning',
        maintenance: 'Maintenance',
        discovered: 'Ready',
        claiming: 'Claiming',
        claimed: 'Claimed',
        connected: 'Connected',
        disconnected: 'Disconnected',
    },
    provisioning: {
        title: 'Gateway Provisioning',
        discoveredGateways: 'Discovered Gateways',
        noGatewaysFound: 'No unclaimed gateways found',
        waitingForGateways: 'Waiting for gateways to announce...',
        hwUid: 'Hardware UID',
        model: 'Model',
        firmware: 'Firmware',
        firstSeen: 'First Seen',
        lastSeen: 'Last Seen',
        viewDetails: 'View Details',
        claim: 'Claim',
        claiming: 'Claiming...',
        claimSuccess: 'Gateway claimed successfully',
        claimError: 'Failed to claim gateway',
        siteLabel: 'Site',
        gatewayCode: 'Gateway Code',
        gatewayCodeRequired: 'Gateway code is required',
        gatewayCodePattern: 'Lowercase letters, numbers, and hyphens only',
        gatewayCodePlaceholder: 'e.g. ctsc-0001',
        gatewayCodeHelp: 'Unique identifier for this gateway (e.g., lobby, gw01, kitchen). Lowercase letters, numbers, and hyphens only.',
    },
    gateways: {
        title: 'Gateways',
        noGatewaysFound: 'No claimed gateways found',
        addGateway: 'Add Gateway',
        gatewayDetails: 'Gateway Details',
        displayName: 'Gateway Name',
        displayNamePlaceholder: 'Optional display name',
        channelMapping: 'Channel Mapping',
        mapChannels: 'Map Channels',
        unmappedChannels: 'Unmapped Channels',
        mappedChannels: 'Mapped Channels',
        selectRoom: 'Select Room',
        channelCode: 'Channel Code',
    },
    rooms: {
        title: 'Rooms',
        noRoomsFound: 'No rooms yet',
        addRoom: 'Add Room',
        editRoom: 'Edit Room',
        deleteRoom: 'Delete Room',
        deleteConfirm: 'Are you sure you want to delete this room?',
        roomCode: 'Room Code',
        roomName: 'Room Name',
        createLock: 'Create default lock',
        building: 'Building',
        floor: 'Floor',
        spaceCategory: 'Space Category',
        roomType: 'Room Type',
        channels: 'Channels',
        noChannels: 'No channels assigned',
        searchPlaceholder: 'Search rooms...',
        floorOrderUnavailable: 'Floor order unavailable - showing floors in an unconfirmed order.',
        noRoomsDescription: 'Rooms will appear here once they are configured in the system.',
        noMatchingRooms: 'No matching rooms',
        noMatchingRoomsDescription: 'Try a different search term.',
        clearSearch: 'Clear search',
    },
    channels: {
        title: 'Channels',
        control: 'Control',
        history: 'History',
        sendCommand: 'Send Command',
        lastValue: 'Last Value',
        lastUpdate: 'Last Update',
        on: 'On',
        off: 'Off',
        level: 'Level',
        temperature: 'Temperature',
    },
    locks: {
        entrance: 'Entrance',
        corridor: 'Corridor',
        connecting: 'Connecting',
        safe: 'Safe',
        minibar: 'Minibar',
        emergency_exit: 'Emergency Exit',
        service: 'Service',
        parking_barrier: 'Parking',
        turnstile: 'Turnstile',
        other: 'Lock',
    },
    cards: {
        guest: 'Guest',
        staff: 'Staff',
        service: 'Service',
        master: 'Master',
        temporary: 'Temporary',
        selectCard: 'Select card type',
        selectRoom: 'Select Room',
        selectLock: 'Select Lock',
        selectLockGroup: 'Select Lock Group',
        selectRoleTemplate: 'Select Role Template',
        optionalRoleTemplate: 'Optional Role Template',
        optionalPrimaryRoom: 'Optional Primary Room',
        linkedRooms: 'Suite or Connecting Rooms',
        addLocks: 'Add Locks (pool, parking, …)',
        addLockGroups: 'Add Lock Groups',
        autoFilledFromGuest: 'Auto-filled from guest, editable',
        roomLocksLabel: 'Room Locks',
        roomLocksHint: 'The entry lock is always included. Tick any additional room locks (e.g. minibar, safe) the card may open.',
        additionalAccess: 'Additional access (optional)',
        otherLocksLabel: 'Other Locks',
        otherLocksHint: 'Individual non-room locks (pool, parking, common areas) written to this card.',
        lockGroupsLabel: 'Lock Groups',
        lockGroupsHint: 'Lock groups written to this card.',
        dialogDescription: 'Read a card on the encoder, confirm its access, then write and save it.',
        retrySave: 'Retry save',
        visitorCardTitleFor: 'Visitor card for {{name}}',
        visitorCardTitle: 'Visitor card',
        visitorCardDescription: "The card is linked to the visit. Its validity may not run past the visit's expected end, and checking the visitor out revokes it.",
        roleTemplateOnlyTitle: 'Access comes from the role template',
        roleTemplateOnlyDescription: 'What this card opens is defined by the role a technician set up. Rooms, locks and lock groups are not chosen here.',
        cardTypeLabel: 'Card Type',
        holderNameLabel: 'Holder Name',
        issueForLabel: 'Issue For',
        optionRoom: 'Room',
        optionLock: 'Lock',
        optionLockGroup: 'Lock Group',
        optionRoleTemplate: 'Role Template',
        primaryRoomLabel: 'Primary Room',
        lockLabel: 'Lock',
        lockGroupLabel: 'Lock Group',
        roleTemplateLabel: 'Role Template',
        linkedRoomsLabel: 'Linked Rooms',
        validUntilLabel: 'Valid Until',
        replaceAndEncode: 'Replace and Encode',
        issueAndEncode: 'Issue and Encode',
        notSavedReportMac: 'Card was not saved. {{reason}}',
        notSavedEncodingFailed: 'Card was not saved because encoding failed: {{reason}}',
        unknownOutcomeSaved: 'The encoder could not confirm the write, so Edge recorded this card as pending. Do not hand it out: keep it here and use Finish writing on the Cards page.',
        issuedAndEncoded: 'Card issued and encoded',
        placeCardFirst: 'Place a card on the reader first',
        databaseSaveFailedDefault: 'Database save failed',
        encodedSaveStillFailed: 'Card is encoded, but saving still failed: {{detail}}. Keep this card here and retry Save; do not encode it again or hand it out until Edge confirms the record.',
        writeReportMissing: 'The encoder did not return a completed write report; the card was not saved.',
        issuingFailedDefault: 'Card issuing failed',
        invalidValidFromPayload: 'Card payload does not contain a valid validFrom field',
        encodedButSaveFailed: 'Card encoded, but database save failed: {{detail}}. Keep this card here and retry Save; do not encode it again or hand it out until Edge confirms the record.',
        writeNotConfirmed: 'The card write could not be confirmed, so nothing was saved. Do not hand this card out: place it on the reader and encode it again.',
        writeUnconfirmedSaveFailed: 'The encoder could not confirm the card write, and saving failed: {{detail}}. Keep this card here and retry Save; do not hand it out until Edge confirms the record.',
        chipTypeNotDetected: 'Card chip type was not detected or is not allowed',
        cardReadyOnReader: 'Card ready on reader',
        placeCardOnReader: 'Place card on reader',
        readerReady: 'Reader ready',
        readerLabel: 'Reader',
        noCardOnReader: 'No card on reader',
        chipType: 'Chip Type',
        unsupportedOrUnknown: 'Unsupported or unknown',
        waitingForTap: 'Waiting for tap',
        titleRoom: 'Issue Room Card',
        titleLock: 'Issue Lock Card',
        titleLockGroup: 'Issue Lock Group Card',
        titleRole: 'Issue Role Card',
        titleReplace: 'Replace Card',
        titleStandalone: 'Issue Card',
        validation: {
            cardUidRequired: 'Tap a card to read its UID',
            chipTypeRequired: 'Tap a supported card to read its chip type',
            cardTypeRequired: 'Card type is required',
            issueScopeRequired: 'Select what this card is for',
            roomRequired: 'Select a room',
            lockRequired: 'Select a lock',
            lockGroupRequired: 'Select a lock group',
            roleTemplateRequired: 'Select a role template',
            validUntilRequired: 'Valid until date is required',
            validUntilTooFar: 'Valid until cannot be more than one year from today',
        },
    },
    /**
     * The User app's Access Control page. Separate from `grms.access`, which is
     * the Access *tab* of a room drawer: that one talks about one room's guest
     * cards, this one about the site's shared-area locks, and they share no
     * string. The page asked for `access.*` keys with inline English defaults
     * long before this block existed, so English rendered correctly and
     * Croatian silently fell back to it — the defect this closes.
     */
    access: {
        searchPlaceholder: 'Search locks, rooms, buildings...',
        lockCount_one: '{{count}} lock',
        lockCount_other: '{{count}} locks',
        noLocksFound: 'No active locks',
        locksNotActive: '{{count}} lock(s) exist but none are set to active. Change lock status to "active" in the admin panel.',
        noLocksDescription: 'Locks will appear here once configured in the admin panel.',
        onlyGuestRoomLocks: 'No shared-area locks',
        onlyGuestRoomLocksDescription: 'Every active lock on this site is a guest-room door. Those are operated from the room, on the Door & keys tab of its room dialog.',
        noSearchResults: 'No locks match your search',
        category: {
            common_area: 'Common Area',
            restricted: 'Restricted',
            service: 'Service',
            guest_room: 'Guest Room',
        },
        unassigned: 'Unassigned',
        lockGroups: 'Lock Groups',
        roleTemplates: 'Role Templates',
        lockGroupMeta_one: '{{count}} lock · ACL {{index}}',
        lockGroupMeta_other: '{{count}} locks · ACL {{index}}',
        roleMeta_one: '{{count}} ACL ID',
        roleMeta_other: '{{count}} ACL IDs',
        housekeepingSuffix: 'housekeeping',
        state: {
            noSignal: 'No signal',
            locked: 'Locked',
            unlocked: 'Unlocked',
            noLockChannel: 'No lock channel',
            reader: 'Reader',
            contactOpen: 'Open',
            contactClosed: 'Closed',
        },
        lockType: {
            entrance: 'Entrance',
            corridor: 'Corridor',
            connecting: 'Connecting',
            emergency_exit: 'Emergency Exit',
            service: 'Service',
            locker: 'Locker',
            elevator: 'Elevator',
            parking_barrier: 'Parking Barrier',
            turnstile: 'Turnstile',
            other: 'Other',
        },
        lock: 'Lock',
        unlock: 'Unlock',
        open: 'Open',
        openPulse: 'Open ({{seconds}}s)',
        locking: 'Locking…',
        unlocking: 'Unlocking…',
        opening: 'Opening…',
        issueCard: 'Issue Card',
        issue: 'Issue',
        replace: 'Replace',
        lockAria: 'Lock {{name}}',
        unlockAria: 'Unlock {{name}}',
        openAria: 'Open {{name}}',
        cardAriaLocked: '{{name}}, locked',
        cardAriaUnlocked: '{{name}}, unlocked',
        activeCards: 'Active cards ({{count}})',
        noActiveCards: 'No active cards',
        waitingForPulse: 'Waiting for the pulse to finish',
        issueRequiresManager: 'Issuing staff and service cards requires manager access',
        operateRequiresPermission: 'Operating locks requires the access_control:operate permission',
        permissionsUnavailable: 'Permissions are unavailable; try again shortly',
        confirm: {
            openTitle: 'Open Lock',
            openBody: 'Open “{{name}}” for {{seconds}}s? It relocks itself when the pulse ends. The action is recorded in the audit log.',
            openConfirm: 'Confirm Open',
            unlockTitle: 'Unlock Lock',
            unlockBody: 'Unlock “{{name}}”? It stays unlocked until someone locks it again, and the action is recorded in the audit log.',
            unlockConfirm: 'Confirm Unlock',
            cancel: 'Cancel',
            sending: 'Sending...',
        },
        activity: {
            title: 'Recent Activity',
            empty: 'No recent access events',
            by: 'By: {{actor}}',
            card: 'Card: {{card}}',
        },
        /** Keyed by the `event_type` enum of BMS-spec `schemas/lock_event.schema.json`. */
        event: {
            access_granted: 'Access granted',
            access_denied: 'Access denied',
            lock_opened: 'Door opened',
            lock_closed: 'Door closed',
            lock_forced_open: 'Forced open',
            lock_left_open: 'Left open',
            lock_status_changed: 'Lock status changed',
            tamper_detected: 'Tamper detected',
            emergency_unlock: 'Emergency unlock',
            manual_lock: 'Remote lock',
            manual_unlock: 'Remote unlock',
        },
        toast: {
            lockSubmitted: 'Relock command submitted for {{lock}}',
            unlockSubmitted: 'Held unlock command submitted for {{lock}}',
            openSubmitted: 'Open command submitted for {{lock}}',
            lockFailed: 'Failed to lock {{lock}}',
            unlockFailed: 'Failed to unlock {{lock}}',
            openFailed: 'Failed to open {{lock}}',
        },
        /**
         * Read generically by `useAccessLabel()` in Admin's
         * `screens/access/labels.ts` (`access.enum.<kind>.<value>`, falling back to
         * the raw code when a value has no key yet) and directly by the User UI's
         * Cards page. `revokeReason` mirrors `cardRevocation.ts`'s `REVOKE_REASONS`.
         */
        enum: {
            revokeReason: {
                lost: 'Lost — reported by the holder',
                stolen: 'Stolen',
                manual: 'Manual — withdrawn by an administrator',
            },
        },
        /**
         * `nfcagent/client.ts`'s `describeEncodeError()` keeps returning English by
         * default for callers that have not moved onto a translator yet
         * (`IssueCardModal`, `CardTapHandler`); its `encodeErrorKey()` returns one
         * of these for a caller that has.
         */
        encodeError: {
            report_mac_unavailable: 'The card may have been written, but the encoder could not authenticate its report. Keep the card, do not hand it out, read it and look it up in Edge before encoding it again.',
            write_request_mac_invalid: 'The encoder refused the write request: its SAM does not hold this site’s current card key, or the request was altered. Nothing was written.',
            write_mac_format_unsupported: 'Edge and NfcAgent run incompatible versions. Update NfcAgent. Nothing was written.',
            card_uid_mismatch: 'The card on the reader is not the card the request was prepared for. Nothing was written.',
            unknown: 'Unknown error',
        },
        /**
         * `useReaderPrecondition()` returns one of these full key paths as its
         * `openDisabledReason`/`submitDisabledReason` (never English text), so a
         * caller translates with `t(reason)` at render — the same pattern
         * `encodeErrorKey()` uses.
         */
        readerPrecondition: {
            materialLoading: 'Loading site card material',
            materialUnavailable: 'Site card material is unavailable',
            agentDisconnected: 'Card Reader Agent is not connected',
            keymaster: 'Reader in use by KeyMaster',
            noSam: 'No SAM detected in the reader',
            samBindingMismatch: 'Reader SAM is bound to a different site',
            samNotSiteBound: 'Reader SAM is not bound to this site',
            samUidUnavailable: 'Reader SAM UID is unavailable',
            placeCard: 'Place a card on the reader',
            cardUidMissing: 'Card UID was not detected',
        },
        /** `common/src/utils/alarmHistory.ts`'s `ALARM_TYPE_LABEL_KEYS` / `ALARM_STATUS_LABEL_KEYS`. */
        alarmType: {
            sos: 'SOS',
            lockForced: 'Lock forced',
            tamper: 'Tamper',
            burglary: 'Burglary',
            hvacDeviation: 'HVAC deviation',
            probeError: 'Probe error',
            motionInVacant: 'Motion in vacant',
            overdueCheckout: 'Overdue checkout',
        },
        alarmStatus: {
            active: 'Active',
            acknowledged: 'Acknowledged',
            resolved: 'Resolved',
        },
    },
    settings: {
        title: 'Settings',
        deviceDescription: 'Preferences for this device.',
        siteConfiguration: 'Site Configuration',
        siteName: 'Site Name',
        siteCode: 'Site Code',
        timezone: 'Timezone',
        profile: 'Profile',
        profileFull: 'Full (Building/Floor/Room)',
        profileSimple: 'Simple (Room only)',
        spaceCategory: 'Default Space Category',
        appearance: 'Appearance',
        darkMode: 'Dark Mode',
        darkModeDescription: 'Use dark theme for the interface',
        language: 'Language',
        languageDescription: 'Select your preferred language',
        connection: 'Connection',
        mqttConnection: 'MQTT Connection',
        mqttConnectionDescription: 'Real-time updates from gateways',
        about: 'About',
    },
    user: {
        selectRoom: 'Select a room to control',
        controls: 'controls',
        noControls: 'No controllable channels in this room',
        noControlsDescription: 'Channels will appear here once they are mapped to this room.',
        scenesDescription: 'Quick scene activation',
        noScenes: 'No scenes configured',
    },
    /** The User app's single-room page (`RoomPage.tsx`), separate from the plural `rooms.*` list keys. */
    room: {
        notFound: 'Room not found',
        notFoundDescription: "This room may have been removed or you don't have access.",
    },
    errors: {
        networkError: 'Network error. Please check your connection.',
        serverError: 'Server error. Please try again later.',
        notFound: 'Resource not found.',
        unauthorized: 'You are not authorized to perform this action.',
        validationError: 'Please check your input and try again.',
        gateway_offline: 'The gateway is offline. Try again when it reconnects.',
        permission_denied: 'You do not have permission to perform this action.',
        not_authenticated: 'Your session has expired. Please sign in again.',
        pending_token_not_permitted: 'Select a site to continue.',
        validation_error: 'Please check the entered information and try again.',
        conflict: 'This action conflicts with the current state. Refresh and try again.',
        inventory_pending: 'The thermostat is updating its settings. Try again in a few seconds.',
        hotelThermostat: {
            zone_not_bound: 'No thermostat is bound to this zone. Ask a technician to bind it.',
            no_confirmed_policy: 'The thermostat has not confirmed its hotel settings yet. Try again shortly.',
            zone_bound_elsewhere: 'This thermostat is already bound to another room zone.',
            not_acknowledgeable: 'There is no refused or conflicting setting to accept.',
            no_device_policy: 'The thermostat has not reported valid settings yet, so there is nothing to take over.',
            stay_reset_owed: 'A stay reset is still owed to this thermostat. Resend it, or discard it explicitly.',
            not_hotel_thermostat: 'This channel is not a commissioned hotel thermostat.',
        },
        room_state_transition_rejected: 'The room is no longer in that state. Refresh and try again.',
        dnd_active: 'Cleaning cannot start while the room is on Do not disturb.',
        room_move_conflict: 'The other room is not vacant.',
        room_not_found: 'The room was not found. It may have been removed.',
        flag_channel_unavailable: 'The room has no device that can clear this request. Clear it in the room.',
        card_not_found: 'The card was not found. It may have been removed.',
        card_type_forbidden: 'Your role cannot issue this type of card.',
        card_uid_in_use: 'This card already holds a key. Revoke it first, or use another card.',
        card_not_retirable: "This card is still someone's live key. Revoke it first, or use another card.",
        card_retired: 'This card is retired and cannot be changed.',
        card_not_mutable: "The card's status does not allow this change.",
        carrier_not_permitted: 'This type of card needs a MIFARE Plus EV2 card.',
        claim_id_missing: 'This site is not set up for cards yet. Contact your administrator.',
        lock_not_found: 'The lock was not found. It may have been removed.',
        device_offline: 'The device is offline. Try again when it reconnects.',
        unexpected: 'Something went wrong. Please try again.',
    },
    // Room type codes (types/roomTypes.ts, BMS-spec space profiles).
    roomTypes: {
        guest_room: 'Guest Room',
        guest_bathroom: 'Guest Bathroom',
        corridor: 'Corridor',
        lobby: 'Lobby',
        reception: 'Reception',
        restaurant: 'Restaurant',
        bar: 'Bar',
        conference_room: 'Conference Room',
        meeting_room: 'Meeting Room',
        gym: 'Gym',
        spa: 'Spa',
        pool_area: 'Pool Area',
        wellness: 'Wellness',
        staff_room: 'Staff Room',
        service_room: 'Service Room',
        wc_public: 'Public Restroom',
        bedroom: 'Bedroom',
        living_room: 'Living Room',
        kitchen: 'Kitchen',
        bathroom: 'Bathroom',
        dining_room: 'Dining Room',
        laundry: 'Laundry',
        outlock_area: 'Outlock Area',
        terrace: 'Terrace',
        storage: 'Storage',
        office: 'Office',
        backoffice: 'Back Office',
        open_space: 'Open Space',
        server_room: 'Server Room',
        break_room: 'Break Room',
        stairwell: 'Stairwell',
        plantroom: 'Plant Room',
        roof_technical: 'Roof Technical',
        workshop: 'Workshop',
        loading_dock: 'Loading Dock',
        warehouse: 'Warehouse',
        control_room: 'Control Room',
        parking: 'Parking',
        technical_room: 'Technical Room',
        electrical_room: 'Electrical Room',
        elevator_lobby: 'Elevator Lobby',
    },
    grms: enGrmsTranslations,
    housekeeping: enHousekeepingTranslations,
};
const hrTranslations = {
    kit: {
        date: 'Datum',
        time: 'Vrijeme (24 h)',
        cancel: 'Odustani',
        confirm: 'Potvrdi',
        close: 'Zatvori',
        save: 'Spremi',
        undo: 'Vrati',
        edit: 'Uredi',
        delete: 'Izbriši',
        remove: 'Ukloni',
        copy: 'Kopiraj',
        copied: 'Kopirano',
        retry: 'Pokušaj ponovno',
        discard: 'Odbaci',
        keepEditing: 'Nastavi uređivanje',
        discardCount_one: 'Odbaciti {{count}} promjenu?',
        discardCount_few: 'Odbaciti {{count}} promjene?',
        discardCount_other: 'Odbaciti {{count}} promjena?',
        changeCount_one: '{{count}} promjena',
        changeCount_few: '{{count}} promjene',
        changeCount_other: '{{count}} promjena',
        search: 'Pretražite…',
        selectPlaceholder: 'Odaberite…',
        clearSelection: 'Očisti odabir',
        clearFilters: 'Očisti filtre',
        noMatches: 'Nema rezultata',
        noMatchesFor: 'Ništa ne odgovara upitu „{{query}}”',
        noResultsTitle: 'Ništa ne odgovara ovim filtrima',
        emptyTitle: 'Još nema ničega',
        rowsPerPage: 'Redaka po stranici',
        pageRange: '{{from}}–{{to}} od {{total}}',
        previousPage: 'Prethodna stranica',
        nextPage: 'Sljedeća stranica',
        selectAll: 'Odaberi sve',
        selectRow: 'Odaberi redak',
        expandRow: 'Proširi redak',
        collapseRow: 'Sažmi redak',
        selectedCount_one: '{{count}} odabran',
        selectedCount_few: '{{count}} odabrana',
        selectedCount_other: '{{count}} odabranih',
        moreActions: 'Više radnji',
        increment: 'Povećaj',
        decrement: 'Smanji',
        dropzoneTitle: 'Ispustite datoteku ovdje ili kliknite za odabir',
        dropzoneMultiple: 'Ispustite datoteke ovdje ili kliknite za odabir',
        fileTooLarge: 'Datoteka {{name}} veća je od {{max}}',
        typeToConfirm: 'Upišite {{value}} za potvrdu',
        characters: '{{count}} / {{max}}',
        loading: 'Učitavanje…',
        breadcrumb: 'Navigacijski put',
        more: 'Više',
        enterKey: 'Enter',
        escKey: 'Esc',
    },
    common: {
        loading: 'Učitavanje...',
        error: 'Greška',
        save: 'Spremi',
        cancel: 'Odustani',
        delete: 'Izbriši',
        edit: 'Uredi',
        create: 'Stvori',
        confirm: 'Potvrdi',
        search: 'Pretraži',
        actions: 'Radnje',
        status: 'Status',
        name: 'Naziv',
        type: 'Vrsta',
        value: 'Vrijednost',
        enabled: 'Omogućeno',
        disabled: 'Onemogućeno',
        yes: 'Da',
        no: 'Ne',
        unknown: 'Nepoznato',
        noData: 'Nema dostupnih podataka',
        refresh: 'Osvježi',
        back: 'Natrag',
        next: 'Sljedeće',
        previous: 'Prethodno',
        finish: 'Završi',
        close: 'Zatvori',
    },
    auth: {
        // The credential half of this block was missing until now: `hr` covered
        // only the site-selection flow it was written for, so a reader who chose
        // Hrvatski still got "Username" and "Password" from the English fallback.
        login: 'Prijavi se',
        logout: 'Odjava',
        username: 'Korisničko ime',
        password: 'Lozinka',
        loginTitle: 'Prijava',
        loginSuccess: 'Prijava uspješna!',
        usernameRequired: 'Unesite korisničko ime',
        passwordRequired: 'Unesite lozinku',
        loginError: 'Neispravno korisničko ime ili lozinka',
        sessionExpired: 'Vaša sesija je istekla. Prijavite se ponovno.',
        siteSelection: 'Odabir lokacije',
        selectSite: 'Odaberite lokaciju…',
        selectSiteAction: 'Odaberi lokaciju',
        loadingSites: 'Učitavanje lokacija…',
        siteRequired: 'Odaberite lokaciju',
        createNewSite: 'Stvori novu lokaciju…',
        newSiteName: 'Naziv lokacije',
        newSiteNamePlaceholder: 'npr. Glavna zgrada',
        newSiteNameRequired: 'Unesite naziv lokacije',
        newSiteLocked: 'Profil i kategorija lokacije zaključani su nakon stvaranja.',
        siteCreated: 'Lokacija "{{name}}" je stvorena',
        noSiteAccessTitle: 'Nema pristupa lokaciji',
        noSiteAccessBody: 'Vaš račun nije dodijeljen nijednoj lokaciji. Zatražite od administratora pristup, a zatim se ponovno prijavite.',
        backToSignIn: 'Natrag na prijavu',
        siteCategory: {
            hospitality: 'Ugostiteljstvo',
            residential: 'Stambeno',
            commercial: 'Komercijalno',
        },
    },
    activation: {
        title: 'Aktiviraj lokaciju',
        intro: 'Ovaj Edge još nema lokaciju. Aktivirajte je kodovima koje ste dobili od Corteha; Edge preuzima naziv, vrstu i vremensku zonu lokacije od Corteha.',
        method: 'Način aktivacije',
        setup: 'Kod za postavljanje',
        setupHint: 'Nova lokacija: Claim Code i četveroznamenkasti kod za postavljanje od Corteha.',
        restore: 'Vrati iz sigurnosne kopije',
        restoreHint: 'Ponovno izgradi lokaciju na ovoj novoj instalaciji iz njezine najnovije sigurnosne kopije konfiguracije kod Corteha.',
        file: 'Datoteka lokacije',
        fileHint: 'Datoteka lokacije koju je poslao Corteh, kada se kodovi ne mogu koristiti.',
        claimCode: 'Claim Code',
        claimCodeHint: 'Osam slova, u obliku XXXX-XXXX.',
        setupCode: 'Kod za postavljanje',
        restoreCode: 'Kod za vraćanje',
        codeHint: 'Četiri znamenke, jednokratno.',
        activateAction: 'Aktiviraj lokaciju',
        restoreAction: 'Vrati lokaciju',
        chooseFile: 'Odaberi datoteku lokacije…',
        copyDetails: 'Kopiraj detalje',
        activated: 'Lokacija {{site}} je aktivirana',
        restored: 'Lokacija {{site}} je vraćena iz sigurnosne kopije',
        reissue: 'Corteh mora dovršiti rotaciju ključa lokacije, a zatim se sve kartice moraju ponovno izdati.',
        signInAgain: 'Lokacija je spremna; ponovno se prijavite da biste je otvorili.',
        continue: 'Nastavi',
        noSiteTitle: 'Ovaj Edge još nema lokaciju',
        noSiteBody: 'Najprije aktivirajte lokaciju; sve ostalo ovisi o njoj.',
        refusal: {
            site_code_invalid: 'Ovaj kod nije ispravan ili se više ne može koristiti; zatražite novi kod od Corteha.',
            site_mode_mismatch: 'Ova je lokacija registrirana za drugi proizvod; obratite se Cortehu.',
            site_not_found: 'Lokacija nije pronađena; provjerite Claim Code.',
            site_file_unavailable: 'Linda nema upotrebljivu datoteku za ovu lokaciju; obratite se Cortehu.',
            backup_unavailable: 'Nema upotrebljive sigurnosne kopije u oblaku; odaberite lokalnu kopiju ili se obratite Cortehu.',
            backup_member_unknown: 'Sigurnosna kopija sadrži nepodržane podatke; ažurirajte aplikaciju i pokušajte ponovno.',
            backup_invalid: 'Ova sigurnosna kopija konfiguracije nije valjana; zadržite lokalnu kopiju i obratite se Cortehu.',
            site_restore_not_empty: 'Aktivirajte ili vratite lokaciju na novoj instalaciji; ova već sadrži konfiguraciju.',
            site_package_invalid: 'Ova datoteka lokacije nije valjana; zatražite zamjensku od Corteha.',
            site_package_claim_mismatch: 'Ova datoteka lokacije pripada drugoj lokaciji.',
            site_certificate_invalid: 'Certifikat ove lokacije nije moguće provjeriti; obratite se Cortehu.',
            site_certificate_claim_mismatch: 'Ovaj certifikat pripada drugoj lokaciji.',
            site_certificate_signer_mismatch: 'Ova datoteka lokacije navodi drugi ključ za potpisivanje; zatražite zamjensku od Corteha.',
            signing_key_import_failed: 'Ova instalacija nije mogla spremiti ključ lokacije; obratite se Cortehu.',
            rate_limited: 'Previše pokušaja; pričekajte prije ponovnog pokušaja.',
            service_unavailable: 'Linda nije dostupna; nastavite raditi lokalno i pokušajte kasnije.',
            pending_token_not_permitted: 'Prijava je istekla; ponovno se prijavite i ponovite aktivaciju.',
            not_authenticated: 'Prijava je istekla; ponovno se prijavite i ponovite aktivaciju.',
            permission_denied: 'Ovaj račun ne smije aktivirati lokaciju.',
            unknown: 'Lokaciju nije bilo moguće aktivirati; obratite se Cortehu.',
        },
    },
    nav: {
        dashboard: 'Nadzorna ploča',
        desk: 'Recepcija',
        activity: 'Aktivnost',
        rooms: 'Sobe',
        gateways: 'Pristupnici',
        provisioning: 'Uspostava',
        settings: 'Postavke',
        siteConfig: 'Konfiguracija lokacije',
        users: 'Korisnici',
        home: 'Početna',
        scenes: 'Scene',
        housekeeping: 'Domaćinstvo',
        accessControl: 'Kontrola pristupa',
        frontOffice: 'Recepcija',
        visitors: 'Posjetitelji',
        cards: 'Kartice',
        reports: 'Izvještaji',
        'hotel-settings': 'Postavke hotela',
        cardTapOpenSearch: 'Otvori pretragu pri očitanju kartice',
        switchToAdmin: 'Otvori administracijsko sučelje',
        logout: 'Odjava',
    },
    status: {
        online: 'Na mreži',
        offline: 'Izvan mreže',
        error: 'Greška',
        updating: 'Ažuriranje',
        active: 'Aktivan',
        inactive: 'Neaktivan',
        commissioning: 'Uspostava',
        maintenance: 'Održavanje',
        discovered: 'Spreman',
        claiming: 'Preuzimanje',
        claimed: 'Preuzeto',
        connected: 'Povezano',
        disconnected: 'Odspojeno',
    },
    provisioning: {
        title: 'Uspostava pristupnika',
        discoveredGateways: 'Otkriveni pristupnici',
        noGatewaysFound: 'Nema nepreuzetih pristupnika',
        waitingForGateways: 'Čekanje na najavu pristupnika...',
        hwUid: 'Hardverski UID',
        model: 'Model',
        firmware: 'Firmver',
        firstSeen: 'Prvi put viđen',
        lastSeen: 'Zadnji put viđen',
        viewDetails: 'Prikaži detalje',
        claim: 'Preuzmi',
        claiming: 'Preuzimanje...',
        claimSuccess: 'Pristupnik je uspješno preuzet',
        claimError: 'Preuzimanje pristupnika nije uspjelo',
        siteLabel: 'Lokacija',
        gatewayCode: 'Šifra pristupnika',
        gatewayCodeRequired: 'Šifra pristupnika je obavezna',
        gatewayCodePattern: 'Samo mala slova, brojevi i crtice',
        gatewayCodePlaceholder: 'npr. ctsc-0001',
        gatewayCodeHelp: 'Jedinstveni identifikator ovog pristupnika (npr. lobby, gw01, kuhinja). Samo mala slova, brojevi i crtice.',
    },
    gateways: {
        title: 'Pristupnici',
        noGatewaysFound: 'Nema preuzetih pristupnika',
        addGateway: 'Dodaj pristupnik',
        gatewayDetails: 'Detalji pristupnika',
        displayName: 'Naziv pristupnika',
        displayNamePlaceholder: 'Neobavezni naziv za prikaz',
        channelMapping: 'Mapiranje kanala',
        mapChannels: 'Mapiraj kanale',
        unmappedChannels: 'Nemapirani kanali',
        mappedChannels: 'Mapirani kanali',
        selectRoom: 'Odaberi sobu',
        channelCode: 'Šifra kanala',
    },
    rooms: {
        title: 'Sobe',
        noRoomsFound: 'Još nema soba',
        addRoom: 'Dodaj sobu',
        editRoom: 'Uredi sobu',
        deleteRoom: 'Izbriši sobu',
        deleteConfirm: 'Jeste li sigurni da želite izbrisati ovu sobu?',
        roomCode: 'Šifra sobe',
        roomName: 'Naziv sobe',
        createLock: 'Stvori zadanu bravu',
        building: 'Zgrada',
        floor: 'Kat',
        spaceCategory: 'Kategorija prostora',
        roomType: 'Vrsta sobe',
        channels: 'Kanali',
        noChannels: 'Nema dodijeljenih kanala',
        searchPlaceholder: 'Pretražite sobe...',
        floorOrderUnavailable: 'Redoslijed katova nije potvrđen - katovi se prikazuju u nepotvrđenom redoslijedu.',
        noRoomsDescription: 'Sobe će se pojaviti ovdje nakon što budu konfigurirane u sustavu.',
        noMatchingRooms: 'Nema odgovarajućih soba',
        noMatchingRoomsDescription: 'Pokušajte s drugim pojmom za pretraživanje.',
        clearSearch: 'Očisti pretragu',
    },
    channels: {
        title: 'Kanali',
        control: 'Upravljanje',
        history: 'Povijest',
        sendCommand: 'Pošalji naredbu',
        lastValue: 'Zadnja vrijednost',
        lastUpdate: 'Zadnje ažuriranje',
        on: 'Uključeno',
        off: 'Isključeno',
        level: 'Razina',
        temperature: 'Temperatura',
    },
    locks: {
        entrance: 'Ulaz',
        corridor: 'Hodnik',
        connecting: 'Prolazna',
        safe: 'Sef',
        minibar: 'Minibar',
        emergency_exit: 'Izlaz u nuždi',
        service: 'Servis',
        parking_barrier: 'Parking',
        turnstile: 'Okretnica',
        other: 'Brava',
    },
    cards: {
        guest: 'Gost',
        staff: 'Osoblje',
        service: 'Servis',
        master: 'Glavna',
        temporary: 'Privremena',
        selectCard: 'Odaberite vrstu kartice',
        selectRoom: 'Odaberi sobu',
        selectLock: 'Odaberi bravu',
        selectLockGroup: 'Odaberi grupu brava',
        selectRoleTemplate: 'Odaberi predložak uloge',
        optionalRoleTemplate: 'Neobavezni predložak uloge',
        optionalPrimaryRoom: 'Neobavezna primarna soba',
        linkedRooms: 'Apartman ili povezane sobe',
        addLocks: 'Dodaj brave (bazen, parking, …)',
        addLockGroups: 'Dodaj grupe brava',
        autoFilledFromGuest: 'Automatski popunjeno prema podacima gosta, može se urediti',
        roomLocksLabel: 'Brave sobe',
        roomLocksHint: 'Ulazna brava je uvijek uključena. Označite dodatne brave sobe (npr. minibar, sef) koje kartica smije otvoriti.',
        additionalAccess: 'Dodatni pristup (neobavezno)',
        otherLocksLabel: 'Ostale brave',
        otherLocksHint: 'Pojedinačne brave izvan sobe (bazen, parking, zajednički prostori) upisane na ovu karticu.',
        lockGroupsLabel: 'Grupe brava',
        lockGroupsHint: 'Grupe brava upisane na ovu karticu.',
        dialogDescription: 'Očitajte karticu na koderu, potvrdite njezin pristup, a zatim je zapišite i spremite.',
        retrySave: 'Pokušaj spremiti ponovno',
        visitorCardTitleFor: 'Kartica posjetitelja za {{name}}',
        visitorCardTitle: 'Kartica posjetitelja',
        visitorCardDescription: 'Kartica je povezana s posjetom. Njezina valjanost ne može trajati dulje od očekivanog kraja posjeta, a odjava posjetitelja je opoziva.',
        roleTemplateOnlyTitle: 'Pristup dolazi iz predloška uloge',
        roleTemplateOnlyDescription: 'Što ova kartica otvara određuje uloga koju je postavio tehničar. Sobe, brave i grupe brava se ovdje ne biraju.',
        cardTypeLabel: 'Vrsta kartice',
        holderNameLabel: 'Ime nositelja',
        issueForLabel: 'Izdaj za',
        optionRoom: 'Soba',
        optionLock: 'Brava',
        optionLockGroup: 'Grupa brava',
        optionRoleTemplate: 'Predložak uloge',
        primaryRoomLabel: 'Primarna soba',
        lockLabel: 'Brava',
        lockGroupLabel: 'Grupa brava',
        roleTemplateLabel: 'Predložak uloge',
        linkedRoomsLabel: 'Povezane sobe',
        validUntilLabel: 'Vrijedi do',
        replaceAndEncode: 'Zamijeni i kodiraj',
        issueAndEncode: 'Izdaj i kodiraj',
        notSavedReportMac: 'Kartica nije spremljena. {{reason}}',
        notSavedEncodingFailed: 'Kartica nije spremljena jer kodiranje nije uspjelo: {{reason}}',
        unknownOutcomeSaved: 'Koder nije mogao potvrditi zapis, pa je Edge ovu karticu zabilježio kao karticu na čekanju. Nemojte je predati: zadržite je ovdje i upotrijebite Dovrši zapisivanje na stranici Kartice.',
        issuedAndEncoded: 'Kartica izdana i kodirana',
        placeCardFirst: 'Najprije približite karticu čitaču',
        databaseSaveFailedDefault: 'Spremanje u bazu nije uspjelo',
        encodedSaveStillFailed: 'Kartica je kodirana, ali spremanje ipak nije uspjelo: {{detail}}. Zadržite ovu karticu ovdje i ponovite spremanje; nemojte je ponovno kodirati ni predati.',
        writeReportMissing: 'Koder nije vratio dovršeno izvješće o zapisu; kartica nije spremljena.',
        issuingFailedDefault: 'Izdavanje kartice nije uspjelo',
        invalidValidFromPayload: 'Podaci kartice ne sadrže valjano polje validFrom',
        encodedButSaveFailed: 'Kartica je kodirana, ali spremanje u bazu nije uspjelo: {{detail}}. Zadržite ovu karticu ovdje i ponovite spremanje; nemojte je ponovno kodirati ni predati.',
        writeNotConfirmed: 'Upis na karticu nije se mogao potvrditi pa ništa nije spremljeno. Nemojte predati ovu karticu: stavite je na čitač i kodirajte je ponovno.',
        writeUnconfirmedSaveFailed: 'Kodirač nije potvrdio upis na karticu, a spremanje nije uspjelo: {{detail}}. Zadržite ovu karticu ovdje i ponovite spremanje; nemojte je predati dok Edge ne potvrdi zapis.',
        chipTypeNotDetected: 'Vrsta čipa kartice nije otkrivena ili nije dopuštena',
        cardReadyOnReader: 'Kartica spremna na čitaču',
        placeCardOnReader: 'Približite karticu čitaču',
        readerReady: 'Čitač spreman',
        readerLabel: 'Čitač',
        noCardOnReader: 'Nema kartice na čitaču',
        chipType: 'Vrsta čipa',
        unsupportedOrUnknown: 'Nepodržano ili nepoznato',
        waitingForTap: 'Čeka se približavanje',
        titleRoom: 'Izdaj karticu sobe',
        titleLock: 'Izdaj karticu brave',
        titleLockGroup: 'Izdaj karticu grupe brava',
        titleRole: 'Izdaj karticu uloge',
        titleReplace: 'Zamijeni karticu',
        titleStandalone: 'Izdaj karticu',
        validation: {
            cardUidRequired: 'Približite karticu za očitanje UID-a',
            chipTypeRequired: 'Približite podržanu karticu za očitanje vrste čipa',
            cardTypeRequired: 'Vrsta kartice je obavezna',
            issueScopeRequired: 'Odaberite za što je ova kartica',
            roomRequired: 'Odaberite sobu',
            lockRequired: 'Odaberite bravu',
            lockGroupRequired: 'Odaberite grupu brava',
            roleTemplateRequired: 'Odaberite predložak uloge',
            validUntilRequired: 'Datum isteka valjanosti je obavezan',
            validUntilTooFar: 'Valjanost ne može trajati dulje od godinu dana od danas',
        },
    },
    // Croatian runs materially longer than English here — "Nema signala",
    // "Otključano", "Izdaj karticu" — so the Access Control page's layout is
    // sized against these strings, not against the English ones.
    access: {
        searchPlaceholder: 'Pretraži brave, sobe, zgrade...',
        lockCount_one: '{{count}} brava',
        lockCount_few: '{{count}} brave',
        lockCount_other: '{{count}} brava',
        noLocksFound: 'Nema aktivnih brava',
        locksNotActive: 'Postoji ovoliko brava: {{count}}, ali nijedna nije postavljena na aktivno. Promijenite status brave u „aktivno” u administratorskoj ploči.',
        noLocksDescription: 'Brave će se pojaviti ovdje nakon konfiguriranja u administratorskoj ploči.',
        onlyGuestRoomLocks: 'Nema brava zajedničkih prostora',
        onlyGuestRoomLocksDescription: 'Sve aktivne brave na ovoj lokaciji su vrata gostinjskih soba. Njima se upravlja iz sobe, na kartici Vrata i ključevi u prozoru te sobe.',
        noSearchResults: 'Nijedna brava ne odgovara pretrazi',
        category: {
            common_area: 'Zajednički prostor',
            restricted: 'Ograničen pristup',
            service: 'Servis',
            guest_room: 'Gostinjska soba',
        },
        unassigned: 'Nedodijeljeno',
        lockGroups: 'Grupe brava',
        roleTemplates: 'Predlošci uloga',
        lockGroupMeta_one: '{{count}} brava · ACL {{index}}',
        lockGroupMeta_few: '{{count}} brave · ACL {{index}}',
        lockGroupMeta_other: '{{count}} brava · ACL {{index}}',
        roleMeta_one: '{{count}} ACL oznaka',
        roleMeta_few: '{{count}} ACL oznake',
        roleMeta_other: '{{count}} ACL oznaka',
        housekeepingSuffix: 'domaćinstvo',
        state: {
            noSignal: 'Nema signala',
            locked: 'Zaključano',
            unlocked: 'Otključano',
            noLockChannel: 'Nema kanala brave',
            reader: 'Čitač',
            contactOpen: 'Otvoreno',
            contactClosed: 'Zatvoreno',
        },
        lockType: {
            entrance: 'Ulaz',
            corridor: 'Hodnik',
            connecting: 'Prolazna',
            emergency_exit: 'Izlaz u nuždi',
            service: 'Servis',
            locker: 'Ormarić',
            elevator: 'Dizalo',
            parking_barrier: 'Parkirna rampa',
            turnstile: 'Okretnica',
            other: 'Ostalo',
        },
        lock: 'Zaključaj',
        unlock: 'Otključaj',
        open: 'Otvori',
        openPulse: 'Otvori ({{seconds}} s)',
        locking: 'Zaključavanje…',
        unlocking: 'Otključavanje…',
        opening: 'Otvaranje…',
        issueCard: 'Izdaj karticu',
        issue: 'Izdaj',
        replace: 'Zamijeni',
        lockAria: 'Zaključaj {{name}}',
        unlockAria: 'Otključaj {{name}}',
        openAria: 'Otvori {{name}}',
        cardAriaLocked: '{{name}}, zaključano',
        cardAriaUnlocked: '{{name}}, otključano',
        activeCards: 'Aktivne kartice ({{count}})',
        noActiveCards: 'Nema aktivnih kartica',
        waitingForPulse: 'Čeka se završetak impulsa',
        issueRequiresManager: 'Izdavanje kartica osoblja i servisa zahtijeva ovlasti voditelja',
        operateRequiresPermission: 'Upravljanje bravama zahtijeva dopuštenje access_control:operate',
        permissionsUnavailable: 'Dopuštenja nisu dostupna; pokušajte ponovno uskoro',
        confirm: {
            openTitle: 'Otvori bravu',
            openBody: 'Otvoriti „{{name}}” na {{seconds}} s? Brava se sama ponovno zaključava po završetku impulsa. Radnja se bilježi u dnevnik revizije.',
            openConfirm: 'Potvrdi otvaranje',
            unlockTitle: 'Otključaj bravu',
            unlockBody: 'Otključati „{{name}}”? Ostat će otključana dok je netko ponovno ne zaključa, a radnja se bilježi u dnevnik revizije.',
            unlockConfirm: 'Potvrdi otključavanje',
            cancel: 'Odustani',
            sending: 'Slanje...',
        },
        activity: {
            title: 'Nedavna aktivnost',
            empty: 'Nema nedavnih pristupnih događaja',
            by: 'Izvršio/la: {{actor}}',
            card: 'Kartica: {{card}}',
        },
        event: {
            access_granted: 'Pristup odobren',
            access_denied: 'Pristup odbijen',
            lock_opened: 'Vrata otvorena',
            lock_closed: 'Vrata zatvorena',
            lock_forced_open: 'Nasilno otvaranje',
            lock_left_open: 'Ostavljeno otvoreno',
            lock_status_changed: 'Promijenjen status brave',
            tamper_detected: 'Otkriveno neovlašteno diranje',
            emergency_unlock: 'Otključavanje u nuždi',
            manual_lock: 'Daljinsko zaključavanje',
            manual_unlock: 'Daljinsko otključavanje',
        },
        toast: {
            lockSubmitted: 'Naredba zaključavanja poslana za: {{lock}}',
            unlockSubmitted: 'Naredba trajnog otključavanja poslana za: {{lock}}',
            openSubmitted: 'Naredba otvaranja poslana za: {{lock}}',
            lockFailed: 'Zaključavanje nije uspjelo: {{lock}}',
            unlockFailed: 'Otključavanje nije uspjelo: {{lock}}',
            openFailed: 'Otvaranje nije uspjelo: {{lock}}',
        },
        enum: {
            revokeReason: {
                lost: 'Izgubljena — prijavio nositelj',
                stolen: 'Ukradena',
                manual: 'Ručno — povukao administrator',
            },
        },
        encodeError: {
            report_mac_unavailable: 'Kartica je možda zapisana, ali enkoder nije mogao potvrditi izvješće o njoj. Zadržite karticu, ne izdajte je, pročitajte je i provjerite u Edgeu prije ponovnog zapisivanja.',
            write_request_mac_invalid: 'Enkoder je odbio zahtjev za zapis: njegov SAM ne sadrži trenutni ključ kartice ove lokacije, ili je zahtjev izmijenjen. Ništa nije zapisano.',
            write_mac_format_unsupported: 'Edge i NfcAgent koriste nekompatibilne verzije. Ažurirajte NfcAgent. Ništa nije zapisano.',
            card_uid_mismatch: 'Kartica na čitaču nije kartica za koju je zahtjev pripremljen. Ništa nije zapisano.',
            unknown: 'Nepoznata pogreška',
        },
        readerPrecondition: {
            materialLoading: 'Učitavanje materijala kartice lokacije',
            materialUnavailable: 'Materijal kartice lokacije nije dostupan',
            agentDisconnected: 'Agent čitača kartica nije povezan',
            keymaster: 'KeyMaster koristi čitač',
            noSam: 'SAM nije otkriven u čitaču',
            samBindingMismatch: 'SAM čitača je vezan uz drugu lokaciju',
            samNotSiteBound: 'SAM čitača nije vezan uz ovu lokaciju',
            samUidUnavailable: 'UID SAM-a čitača nije dostupan',
            placeCard: 'Približite karticu čitaču',
            cardUidMissing: 'UID kartice nije otkriven',
        },
        alarmType: {
            sos: 'SOS',
            lockForced: 'Nasilno otvaranje brave',
            tamper: 'Neovlašteno diranje',
            burglary: 'Provala',
            hvacDeviation: 'Odstupanje HVAC-a',
            probeError: 'Greška senzora',
            motionInVacant: 'Pokret u praznoj sobi',
            overdueCheckout: 'Zakašnjela odjava',
        },
        alarmStatus: {
            active: 'Aktivan',
            acknowledged: 'Potvrđen',
            resolved: 'Riješen',
        },
    },
    settings: {
        title: 'Postavke',
        deviceDescription: 'Postavke za ovaj uređaj.',
        siteConfiguration: 'Konfiguracija lokacije',
        siteName: 'Naziv lokacije',
        siteCode: 'Šifra lokacije',
        timezone: 'Vremenska zona',
        profile: 'Profil',
        profileFull: 'Potpuni (zgrada/kat/soba)',
        profileSimple: 'Jednostavni (samo soba)',
        spaceCategory: 'Zadana kategorija prostora',
        appearance: 'Izgled',
        darkMode: 'Tamni način',
        darkModeDescription: 'Koristite tamnu temu za sučelje',
        language: 'Jezik',
        languageDescription: 'Odaberite željeni jezik',
        connection: 'Veza',
        mqttConnection: 'MQTT veza',
        mqttConnectionDescription: 'Ažuriranja u stvarnom vremenu s pristupnika',
        about: 'O aplikaciji',
    },
    user: {
        selectRoom: 'Odaberite sobu za upravljanje',
        controls: 'kontrole',
        noControls: 'Nema kanala kojima se može upravljati u ovoj sobi',
        noControlsDescription: 'Kanali će se pojaviti ovdje nakon što budu mapirani na ovu sobu.',
        scenesDescription: 'Brza aktivacija scene',
        noScenes: 'Nema konfiguriranih scena',
    },
    room: {
        notFound: 'Soba nije pronađena',
        notFoundDescription: 'Ova soba je možda uklonjena ili nemate pristup.',
    },
    errors: {
        networkError: 'Greška mreže. Provjerite svoju vezu.',
        serverError: 'Greška poslužitelja. Pokušajte ponovno kasnije.',
        notFound: 'Resurs nije pronađen.',
        unauthorized: 'Nemate ovlaštenje za ovu radnju.',
        validationError: 'Provjerite unesene podatke i pokušajte ponovno.',
        gateway_offline: 'Pristupnik je izvan mreže. Pokušajte ponovno kada se poveže.',
        permission_denied: 'Nemate dopuštenje za ovu radnju.',
        not_authenticated: 'Vaša sesija je istekla. Prijavite se ponovno.',
        pending_token_not_permitted: 'Odaberite lokaciju za nastavak.',
        validation_error: 'Provjerite unesene podatke i pokušajte ponovno.',
        conflict: 'Ova radnja je u sukobu s trenutnim stanjem. Osvježite i pokušajte ponovno.',
        inventory_pending: 'Termostat ažurira svoje postavke. Pokušajte ponovno za nekoliko sekundi.',
        hotelThermostat: {
            zone_not_bound: 'Nijedan termostat nije povezan s ovom zonom. Zatražite od tehničara da ga poveže.',
            no_confirmed_policy: 'Termostat još nije potvrdio hotelske postavke. Pokušajte ponovno uskoro.',
            zone_bound_elsewhere: 'Ovaj termostat je već povezan s drugom zonom sobe.',
            not_acknowledgeable: 'Nema odbijene ni sukobljene postavke za prihvaćanje.',
            no_device_policy: 'Termostat još nije javio valjane postavke pa nema što preuzeti.',
            stay_reset_owed: 'Termostatu se još duguje poništavanje boravka. Pošaljite ga ponovno ili ga izričito odbacite.',
            not_hotel_thermostat: 'Ovaj kanal nije hotelski termostat pušten u rad.',
        },
        room_state_transition_rejected: 'Soba više nije u tom stanju. Osvježite i pokušajte ponovno.',
        dnd_active: 'Čišćenje ne može početi dok je soba na Ne uznemiravaj.',
        room_move_conflict: 'Druga soba nije slobodna.',
        room_not_found: 'Soba nije pronađena. Možda je uklonjena.',
        flag_channel_unavailable: 'Soba nema uređaj koji može poništiti ovaj zahtjev. Poništite ga u sobi.',
        card_not_found: 'Kartica nije pronađena. Možda je uklonjena.',
        card_type_forbidden: 'Vaša uloga ne može izdati ovu vrstu kartice.',
        card_uid_in_use: 'Ova kartica već nosi ključ. Najprije je opozovite ili koristite drugu karticu.',
        card_not_retirable: 'Ova kartica je još nečiji važeći ključ. Najprije je opozovite ili koristite drugu karticu.',
        card_retired: 'Ova kartica je umirovljena i ne može se mijenjati.',
        card_not_mutable: 'Status kartice ne dopušta ovu promjenu.',
        carrier_not_permitted: 'Ova vrsta kartice traži karticu MIFARE Plus EV2.',
        claim_id_missing: 'Lokacija još nije postavljena za kartice. Obratite se administratoru.',
        lock_not_found: 'Brava nije pronađena. Možda je uklonjena.',
        device_offline: 'Uređaj je izvan mreže. Pokušajte ponovno kada se poveže.',
        unexpected: 'Nešto je pošlo po zlu. Pokušajte ponovno.',
    },
    // Room type codes (types/roomTypes.ts, BMS-spec space profiles).
    roomTypes: {
        guest_room: 'Soba za goste',
        guest_bathroom: 'Kupaonica za goste',
        corridor: 'Hodnik',
        lobby: 'Predvorje',
        reception: 'Recepcija',
        restaurant: 'Restoran',
        bar: 'Bar',
        conference_room: 'Konferencijska dvorana',
        meeting_room: 'Sala za sastanke',
        gym: 'Teretana',
        spa: 'Spa',
        pool_area: 'Bazen',
        wellness: 'Wellness',
        staff_room: 'Prostorija za osoblje',
        service_room: 'Servisna prostorija',
        wc_public: 'Javni toalet',
        bedroom: 'Spavaća soba',
        living_room: 'Dnevni boravak',
        kitchen: 'Kuhinja',
        bathroom: 'Kupaonica',
        dining_room: 'Blagovaonica',
        laundry: 'Praonica rublja',
        outlock_area: 'Vanjski prostor',
        terrace: 'Terasa',
        storage: 'Spremište',
        office: 'Ured',
        backoffice: 'Pozadinski ured',
        open_space: 'Otvoreni prostor',
        server_room: 'Serverska soba',
        break_room: 'Prostorija za odmor',
        stairwell: 'Stubište',
        plantroom: 'Strojarnica',
        roof_technical: 'Tehnički prostor na krovu',
        workshop: 'Radionica',
        loading_dock: 'Utovarna rampa',
        warehouse: 'Skladište',
        control_room: 'Kontrolna soba',
        parking: 'Parkiralište',
        technical_room: 'Tehnička prostorija',
        electrical_room: 'Elektro prostorija',
        elevator_lobby: 'Predvorje dizala',
    },
    grms: hrGrmsTranslations,
    housekeeping: hrHousekeepingTranslations,
};
/**
 * The languages Edge ships. English is the base and the fallback; adding a
 * language is one entry here, one in `LOCALE_LABELS`, and its tree in every
 * table ("Adding a language" in corteh-meta shared/ui-design.md).
 */
export const SUPPORTED_LOCALES = ['en', 'hr'];
/**
 * Each language named in itself, for the language switchers: a reader looking
 * for their own language recognises it written its own way, so these need no key.
 */
export const LOCALE_LABELS = {
    en: 'English',
    hr: 'Hrvatski',
};
export function normalizeLocale(locale) {
    return SUPPORTED_LOCALES.includes(locale ?? '') ? locale : 'en';
}
const LOCALE_TABLES = {
    en: enTranslations,
    hr: hrTranslations,
};
// =============================================================================
// Initialize i18n
// =============================================================================
export function initializeI18n(defaultLocale = 'en') {
    return i18n.use(initReactI18next).init({
        resources: Object.fromEntries(SUPPORTED_LOCALES.map((locale) => [locale, { translation: LOCALE_TABLES[locale] }])),
        lng: normalizeLocale(defaultLocale),
        fallbackLng: 'en',
        interpolation: {
            escapeValue: false, // React already escapes
        },
        react: {
            useSuspense: true,
        },
    });
}
/**
 * Register an application's own namespace — `shell`, `devices`, `rooms` —
 * with its tree in every supported language at start-up, so an application never
 * edits this package's inline tables. Every locale is required: a namespace
 * missing one is a single-locale implementation, which is the defect
 * `t(key, 'English default')` used to hide. Re-registering a namespace
 * replaces its keys (deep-merged), which is what a hot reload wants.
 *
 * Keys are read as `${namespace}.${key}` through the default `translation`
 * namespace, the same way the inline tables are, so `useTranslation()` needs
 * no namespace argument.
 */
export function registerTranslations(namespace, bundle) {
    if (!namespace || namespace.includes('.')) {
        throw new Error(`registerTranslations: "${namespace}" is not a valid namespace`);
    }
    const missing = SUPPORTED_LOCALES.filter((locale) => !bundle[locale]);
    if (missing.length > 0) {
        throw new Error(`registerTranslations: namespace "${namespace}" needs ${missing.join(', ')}`);
    }
    for (const locale of SUPPORTED_LOCALES) {
        i18n.addResourceBundle(locale, 'translation', { [namespace]: bundle[locale] }, true, true);
    }
}
// Re-export i18n instance and hooks
export { i18n };
export { hasTranslationKey, literalTranslationKeys, pluralSuffixes, translationLeaves, translationProblems, } from './parity.js';
export { useTranslation, Trans } from 'react-i18next';
//# sourceMappingURL=index.js.map